import {UserSession} from '../types/analytics'
import {userIdentityService} from './UserIdentityService'

/**
 * SessionManager - Handles user session lifecycle for analytics
 *
 * Manages session creation, extension, timeout, and persistence across browser sessions.
 * Uses a simple approach: one active session per user with automatic timeout.
 */
export class SessionManager {
  private currentSession: UserSession | null = null
  private sessionTimeout: NodeJS.Timeout | null = null
  private readonly SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes
  private readonly STORAGE_KEY = 'analytics_current_session'

  constructor() {
    // Handle browser close/refresh
    window.addEventListener('beforeunload', () => this.endSession())

    // Handle storage changes from other tabs
    window.addEventListener('storage', this.handleStorageChange)
  }

  /**
   * Start a new session or resume existing valid session
   */
  async startSession(): Promise<UserSession> {
    // Check for existing valid session first
    const existingSession = this.loadSessionFromStorage()

    if (existingSession && this.isSessionValid(existingSession)) {
      this.currentSession = existingSession
      this.setupSessionTimeout()
      return existingSession
    }

    // Create new session
    const userIdentity = userIdentityService.getIdentity()
    if (!userIdentity) {
      throw new Error('No user identity found - user registration required')
    }

    const newSession: UserSession = {
      sessionId: crypto.randomUUID(),
      userId: userIdentity.id,
      displayName: userIdentity.email,
      startTime: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      isActive: true,
      metadata: {
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        environment:
          (typeof process !== 'undefined' && process.env?.NODE_ENV) ||
          'development',
        url: window.location.href
      }
    }

    this.currentSession = newSession
    this.saveSessionToStorage(newSession)
    this.setupSessionTimeout()

    return newSession
  }

  /**
   * End the current session
   */
  async endSession(): Promise<void> {
    if (!this.currentSession) return

    this.currentSession.endTime = new Date().toISOString()
    this.currentSession.isActive = false

    // Clear timeout
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout)
      this.sessionTimeout = null
    }

    // Save final session state
    this.saveSessionToStorage(this.currentSession)

    // Clear from memory
    this.currentSession = null
  }

  /**
   * Extend session activity (reset timeout)
   */
  extendSession(): void {
    if (!this.currentSession) return

    // Update last activity time
    this.currentSession.lastActivity = new Date().toISOString()
    this.saveSessionToStorage(this.currentSession)

    // Reset timeout
    this.setupSessionTimeout()
  }

  /**
   * Get current active session
   */
  getCurrentSession(): UserSession | null {
    return this.currentSession
  }

  /**
   * Check if session is valid (not expired)
   */
  private isSessionValid(session: UserSession): boolean {
    if (!session.isActive) return false

    const lastActivity = session.lastActivity || session.startTime
    const timeSinceActivity = Date.now() - new Date(lastActivity).getTime()

    return timeSinceActivity < this.SESSION_TIMEOUT
  }

  /**
   * Setup session timeout
   */
  private setupSessionTimeout(): void {
    // Clear existing timeout
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout)
    }

    // Set new timeout
    this.sessionTimeout = setTimeout(() => {
      this.endSession()
    }, this.SESSION_TIMEOUT)
  }

  /**
   * Save session to localStorage
   */
  private saveSessionToStorage(session: UserSession): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(session))
    } catch (error) {
      console.warn('Failed to save session to localStorage:', error)
    }
  }

  /**
   * Load session from localStorage
   */
  private loadSessionFromStorage(): UserSession | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.warn('Failed to load session from localStorage:', error)
      return null
    }
  }

  /**
   * Clear session from localStorage
   */
  private clearSessionFromStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear session from localStorage:', error)
    }
  }

  /**
   * Handle storage changes from other tabs
   */
  private handleStorageChange = (event: StorageEvent) => {
    if (event.key === this.STORAGE_KEY) {
      if (event.newValue) {
        try {
          // Another tab created/updated a session
          const newSession = JSON.parse(event.newValue)
          this.currentSession = newSession
        } catch (error) {
          console.warn('Failed to parse session from storage event:', error)
          console.log('Event details:')
          console.log(event)
          this.currentSession = null
        }
      } else {
        // Session was ended in another tab
        this.currentSession = null
        if (this.sessionTimeout) {
          clearTimeout(this.sessionTimeout)
          this.sessionTimeout = null
        }
      }
    }
  }
}

// Create singleton instance
export const sessionManager = new SessionManager()
